/**
 * Data layer for `cpalead-integration`. Thin typed fetch wrappers over
 * /api/v1/cpalead-integration. Raw `fetch()` is confined to this file per the
 * dash contract (D6).
 */

export interface CredentialRow {
	id: string;
	cpalead_publisher_id: string;
	cpalead_api_key_preview: string;
	status: string;
	created_at: number;
}

export interface ListCredentialsResponse {
	items: CredentialRow[];
}

export interface CreateCredentialBody {
	cpalead_publisher_id: string;
	cpalead_api_key: string;
}

export interface OfferMapRow {
	id: string;
	tenant_id: string;
	cpalead_offer_id: string;
	trackerity_offer_id: string;
	created_at: number;
}

export interface ListOffersResponse {
	items: OfferMapRow[];
}

export interface CreateOfferBody {
	cpalead_offer_id: string;
	trackerity_offer_id: string;
}

export interface PostbackRow {
	id: string;
	tenant_id: string;
	cpalead_subid: string;
	cpalead_offer_id: string;
	payout_cents: number;
	status: string;
	raw_payload: string;
	created_at: number;
}

export interface ListPostbacksResponse {
	items: PostbackRow[];
}

export interface SyncStatusResponse {
	feature: string;
	has_credentials: boolean;
	has_offer_mappings: boolean;
	has_postbacks: boolean;
	items: unknown[];
}

const BASE = "/api/v1/cpalead-integration";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function listCredentials(): Promise<ListCredentialsResponse> {
	const res = await fetch(`${BASE}/credentials`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListCredentialsResponse>(res);
}

export async function createCredential(
	body: CreateCredentialBody,
): Promise<{ ok: boolean; item: CredentialRow }> {
	const res = await fetch(`${BASE}/credentials`, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<{ ok: boolean; item: CredentialRow }>(res);
}

export async function listOffers(): Promise<ListOffersResponse> {
	const res = await fetch(`${BASE}/offers`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListOffersResponse>(res);
}

export async function createOffer(
	body: CreateOfferBody,
): Promise<{ ok: boolean; item: OfferMapRow }> {
	const res = await fetch(`${BASE}/offers`, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<{ ok: boolean; item: OfferMapRow }>(res);
}

export async function listPostbacks(): Promise<ListPostbacksResponse> {
	const res = await fetch(`${BASE}/postbacks`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListPostbacksResponse>(res);
}

export async function getStatus(): Promise<SyncStatusResponse> {
	const res = await fetch(`${BASE}/`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<SyncStatusResponse>(res);
}

/**
 * Validate that an API key is the minimum length required by the BE (>=8).
 * Prefer >=16 for strong keys; we surface a soft-warn below that.
 */
export function isValidApiKey(key: string): boolean {
	if (typeof key !== "string") return false;
	if (key.length < 8) return false;
	return /^[A-Za-z0-9_\-]+$/.test(key);
}

/**
 * Validate publisher ID (cpalead_publisher_id): non-empty alphanumeric / dash / underscore.
 */
export function isValidPublisherId(id: string): boolean {
	if (typeof id !== "string") return false;
	if (id.length === 0) return false;
	return /^[A-Za-z0-9_\-]+$/.test(id);
}

/**
 * Validate non-empty offer identifier (either cpalead_offer_id or trackerity_offer_id).
 */
export function isValidOfferId(id: string): boolean {
	if (typeof id !== "string") return false;
	if (id.length === 0) return false;
	return /^[A-Za-z0-9_\-]+$/.test(id);
}

export const cpaleadIntegrationQueryKeys = {
	all: ["cpalead-integration"] as const,
	credentials: () => [...cpaleadIntegrationQueryKeys.all, "credentials"] as const,
	offers: () => [...cpaleadIntegrationQueryKeys.all, "offers"] as const,
	postbacks: () => [...cpaleadIntegrationQueryKeys.all, "postbacks"] as const,
	status: () => [...cpaleadIntegrationQueryKeys.all, "status"] as const,
};
